import { createContext, useContext } from 'react';
import { useRef } from 'react';
import { createStore, useStore } from 'zustand';
import { type StoreApi } from 'zustand';
import { IConversationMessage } from '../@types/conversation';

interface IConversationContext {
  loadingMore: 'up' | 'down' | false;
  setLoadingMore: (loadingMore: 'up' | 'down' | false) => void;

  replyTo: IConversationMessage | null;
  setReplyTo: (replyTo: IConversationMessage | null) => void;
  goToMessage: string | undefined;
  setGoToMessage: (messageId: string | undefined) => void;
  loadGoTo: boolean;
  setLoadGoTo: (load: boolean) => void;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  scrollEnd: boolean;
  setScrollEnd: (scrollEnd: boolean) => void;
  showInput: boolean;
  setShowInput: (showInput: boolean) => void;
}

const ConversationContext =
  createContext<StoreApi<IConversationContext> | null>(null);

type Props = {
  children: React.ReactNode;
};

export function ConversationContextProvider({ children }: Props) {
  const storeRef = useRef<StoreApi<IConversationContext>>();
  if (!storeRef.current) {
    storeRef.current = createStore<IConversationContext>((set) => ({
      loadingMore: false,
      setLoadingMore: (loadingMore: 'up' | 'down' | false) =>
        set({ loadingMore }),
      replyTo: null,
      setReplyTo: (replyTo) => set({ replyTo }),
      goToMessage: undefined,
      setGoToMessage: (goToMessage: string | undefined) => set({ goToMessage }),
      loadGoTo: false,
      setLoadGoTo: (loadGoTo: boolean) => set({ loadGoTo }),
      searchTerm: '',
      setSearchTerm: (searchTerm) => set({ searchTerm }),
      scrollEnd: false,
      setScrollEnd: (scrollEnd) => set({ scrollEnd }),
      showInput: true,
      setShowInput: (showInput: boolean) => set({ showInput }),
    }));
  }

  return (
    <ConversationContext.Provider value={storeRef.current}>
      {children}
    </ConversationContext.Provider>
  );
}

export function useConversationContext<T = IConversationContext>(
  selector: (state: IConversationContext) => T = (state) => state as T,
) {
  const context = useContext(ConversationContext);

  if (!context)
    throw new Error(
      'useConversationContext must be use inside ConversationContextProvider',
    );

  return useStore(context, selector);
}
